'use client'

import { Fragment, useState } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { Check, X } from 'lucide-react'
import Image from 'next/image'
import moment from 'moment'
import Lend from '@/assets/lendmn.png'
import { formatPrice, formatDateLong, formatImageURL } from '@/utils/util'
import { FillingCircle, PaymentSchedule } from './StorepayDrawer'

interface LendDrawerProps {
  isOpen: boolean
  onClose: () => void
  productPrice: number
  productName: string
  productImage: string
  vendor?: any
}

const ACCENT = '#fb923c'
const ACCENT_SOFT = 'rgba(251,146,60,0.16)'
const ACCENT_BORDER = 'rgba(251,146,60,0.35)'

export default function LendDrawer({ isOpen, onClose, productPrice, productName, productImage, vendor }: LendDrawerProps) {
  const [selectedPlan, setSelectedPlan] = useState<'30days' | '60days'>('30days')

  const paymentPlans = {
    '30days': { label: '30 хоног', description: '1 хуваах', installments: 1, days: 30 },
    '60days': { label: '60 хоног', description: '4 хуваах', installments: 4, days: 60 },
  }

  const calc = (plan: keyof typeof paymentPlans) => {
    const payments = paymentPlans[plan].installments
    const monthlyAmount = Math.ceil(productPrice / payments)
    return { payments, monthlyAmount }
  }

  const getPaymentDates = (plan: keyof typeof paymentPlans) => {
    const today = moment()
    const payments = paymentPlans[plan].installments
    const dayInterval = paymentPlans[plan].days / payments
    return Array.from({ length: payments }, (_, i) => i === 0 ? today.toDate() : today.clone().add(i * dayInterval, 'days').toDate())
  }

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-[2px]" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 flex justify-end">
          <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="translate-x-full">
            <DialogPanel
              className="relative flex w-full max-w-115 flex-col overflow-hidden rounded-l-2xl"
              style={{
                background: 'linear-gradient(180deg, #16161c 0%, #0c0c10 100%)',
                border: '1px solid rgba(255,255,255,0.13)',
                boxShadow: '0 40px 100px -20px rgba(0,0,0,0.75)',
              }}
            >
              <div
                className="sticky top-0 z-10 flex h-14 items-center justify-between gap-3 px-4"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(12,12,16,0.95)', backdropFilter: 'blur(8px)' }}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Image src={Lend} alt="LendMN" className="h-5 w-fit object-contain" />
                  <div className="min-w-0">
                    <div className="mb-1 text-[10px] font-semibold uppercase leading-none tracking-[0.14em]" style={{ color: '#a8a8b3' }}>Хуваан төлөлт</div>
                    <div className="truncate text-[15px] font-semibold leading-none tracking-[-0.005em] text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>LendMN үйлчилгээ</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full transition-colors cursor-pointer"
                  style={{ color: 'rgba(255,255,255,0.5)' }}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain px-6 pt-6 scrollbar-dark">
                <div className="mb-4">
                  <div className="flex items-center gap-3">
                    <div className="relative w-24 h-24 shrink-0 rounded-lg overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      <img src={formatImageURL(productImage)} alt={productName} className="w-full h-full object-cover object-center" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-[15px] leading-6 text-white line-clamp-2 break-words">{productName}</h3>
                      <p className="text-[15px] font-medium leading-6 mt-1 tabular-nums" style={{ color: 'rgba(255,255,255,0.7)' }}>{formatPrice(productPrice)}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-6 space-y-4">
                  <h3 className="text-[14px] font-medium text-white">Хуваан төлөх нөхцөл</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(paymentPlans).map(([planKey, plan]) => {
                      const isSelected = selectedPlan === planKey
                      const fillPct = 100 / plan.installments
                      return (
                        <div
                          key={planKey}
                          onClick={() => setSelectedPlan(planKey as keyof typeof paymentPlans)}
                          className="rounded-lg px-4 py-3 cursor-pointer transition-colors"
                          style={{
                            background: isSelected ? ACCENT_SOFT : 'rgba(0,0,0,0.3)',
                            border: `1px solid ${isSelected ? ACCENT_BORDER : 'rgba(255,255,255,0.1)'}`,
                          }}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex flex-col min-w-0">
                              <div className="text-[13.5px] font-medium text-white">{plan.label}</div>
                              <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>{plan.description}</div>
                            </div>
                            <FillingCircle fillPercentage={fillPct} paymentCount={plan.installments} size={36} accent={ACCENT} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-[14px] font-medium text-white mb-4">
                    {paymentPlans[selectedPlan].label}т {paymentPlans[selectedPlan].installments} хувааж төлөх
                  </h4>
                  <PaymentSchedule
                    productPrice={productPrice}
                    payments={calc(selectedPlan).payments}
                    monthlyAmount={calc(selectedPlan).monthlyAmount}
                    dates={getPaymentDates(selectedPlan)}
                    accent={ACCENT}
                  />
                </div>

                <ul className="text-[11.5px] mb-6 space-y-2 list-disc pl-5" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  <li>100,000₮-өөс дээш үнэтэй худалдан авалтад LendMN зээлийн үйлчилгээ ашиглах боломжтой.</li>
                  <li>Төлбөрийн сонголтыг LendMN гэж сонгоно.</li>
                  <li>LendMN аппликэйшнээс зээлийг баталгаажуулж захиалгаа дуусгана.</li>
                </ul>
              </div>

              <div className="flex items-center justify-between gap-3 p-3" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.3)' }}>
                <span className="text-[11.5px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  {vendor?.createdAt ? `Дэлгүүр нээгдсэн: ${formatDateLong(new Date(vendor.createdAt))}` : ''}
                </span>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12.5px] font-semibold cursor-pointer transition-opacity hover:opacity-90"
                  style={{ background: 'white', color: '#0a0a0c' }}
                >
                  Ойлголоо <Check className="h-3.5 w-3.5" />
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
